import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { getShapeFromElement } from '@editorUtils'
import { AxiosError } from 'axios'
import { find, reject } from 'lodash-es'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import {
  useGenerateTemplateFiles,
  useGuidToCrossSection,
  useGuidToElementIncludingRips,
} from '@editorHooks'
import { getProjectFiles, getProjectFilesTemplates } from '@queries'
import { postFileMetadata, unlinkProjectFile } from '@mutations'

const useUnlinkFile = (rootGuid: string) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { generateTemplates } = useGenerateTemplateFiles(rootGuid)
  const guidToElement = useGuidToElementIncludingRips()
  const guidToCrossSection = useGuidToCrossSection()

  return useMutation({
    mutationFn: async (fileGuid: string) => {
      const filesKey = getProjectFiles.getKey(projectId, rootGuid)
      const files = queryClient.getQueryData<FilesResult>(filesKey)
      const fileToUnlink = find(files?.files, { guid: fileGuid })
      if (!fileToUnlink?.url) {
        await generateTemplates([fileGuid])
      }

      const file: FileItem = await unlinkProjectFile.request(projectId, rootGuid, fileGuid)
      if (!file.element_guid) return file

      // An unlinked file no longer follows its element: save the element as it is now,
      // so the file is marked as outdated once the element changes
      return postFileMetadata.request(projectId, rootGuid, {
        item_guid: file.guid,
        name: file.name,
        ...(file.additional_data ?? {}),
        _elementData: {
          shape: getShapeFromElement(guidToElement[file.element_guid]),
          crossSection: guidToCrossSection[file.element_guid],
        },
      })
    },
    onSuccess: (file: FileItem) => {
      const filesKey = getProjectFiles.getKey(projectId, rootGuid)

      queryClient.setQueryData<FilesResult>(filesKey, prev => {
        if (!prev) return undefined

        const newFiles = reject(prev.files, { guid: file.guid })

        return {
          ...prev,
          files: [...newFiles, file],
        }
      })
      // The unlinked file no longer occupies its template
      queryClient.invalidateQueries(getProjectFilesTemplates.getKey(projectId, rootGuid))
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Entkoppeln'), { variant: 'error' })
    },
  })
}

export default useUnlinkFile
